import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getProductReviews } from '../lib/api';
import { trackEvent } from '../lib/metaPixel';
import { useSEO, buildProductSchema, buildBreadcrumb } from '../lib/seo';
import './ProductDetailPage.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Planter SVG icons (keyed by planter name, lowercase, spaces stripped) ───
const PlanterIcons = {
  nurserypot: (
    <svg width="44" height="52" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 14h28l-4 32H12L8 14z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="6" y="10" width="32" height="6" rx="2" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.8"/>
      <ellipse cx="22" cy="46" rx="7" ry="2" fill="currentColor" fillOpacity="0.15"/>
    </svg>
  ),
  ceramicpot: (
    <svg width="44" height="52" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 14c0 0-2 10-2 18s5 12 14 12 14-4 14-12-2-18-2-18H10z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="7" y="10" width="30" height="5" rx="2.5" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M14 44h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  ),
  terracotta: (
    <svg width="44" height="52" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 13l3 28h20l3-28H9z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="6" y="9" width="32" height="5" rx="2.5" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M15 41h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M12 20h20M11 27h22" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeOpacity="0.4"/>
    </svg>
  ),
  wovenbasket: (
    <svg width="44" height="52" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 13l4 30h20l4-30H8z" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="6" y="9" width="32" height="5" rx="2" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M9 20h26M10 26h24M11 32h22M12 38h20" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.5"/>
      <path d="M16 14v26M22 14v28M28 14v26" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.3"/>
    </svg>
  ),
  concrete: (
    <svg width="44" height="52" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 13h30l-2 30H9L7 13z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="5" y="9" width="34" height="5" rx="1.5" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.8"/>
      <rect x="14" y="43" width="16" height="3" rx="1" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  default: (
    <svg width="44" height="52" viewBox="0 0 44 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 14h28l-4 32H12L8 14z" fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <rect x="6" y="10" width="32" height="6" rx="2" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.8"/>
    </svg>
  ),
};

const getPlanterIcon = (name = '') => {
  const key = name.toLowerCase().replace(/\s+/g, '');
  return PlanterIcons[key] || PlanterIcons.default;
};

// ── Add-on icons ─────────────────────────────────────────────────────────────
const AddOnIcons = {
  cocopeat: (
    <svg width="40" height="40" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="9" width="32" height="28" rx="3" fill="currentColor" fillOpacity="0.10" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M6 16h32" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="14" cy="24" r="1.6" fill="currentColor" fillOpacity="0.55"/>
      <circle cx="22" cy="28" r="1.6" fill="currentColor" fillOpacity="0.55"/>
      <circle cx="30" cy="23" r="1.6" fill="currentColor" fillOpacity="0.55"/>
      <circle cx="18" cy="32" r="1.6" fill="currentColor" fillOpacity="0.55"/>
      <circle cx="27" cy="32" r="1.6" fill="currentColor" fillOpacity="0.55"/>
    </svg>
  ),
  soilmixture: (
    <svg width="40" height="40" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 9h26l3 5-3 23H9L6 14l3-5z" fill="currentColor" fillOpacity="0.10" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
      <path d="M6 14h32" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M14 21c2 2 4-2 6 0s4-2 6 0 4-2 4 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeOpacity="0.55"/>
      <path d="M13 28c2 2 4-2 6 0s4-2 6 0 4-2 6 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeOpacity="0.4"/>
    </svg>
  ),
  default: (
    <svg width="40" height="40" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="22" cy="22" r="16" fill="currentColor" fillOpacity="0.10" stroke="currentColor" strokeWidth="1.8"/>
      <path d="M22 14v8M22 26v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
};

const getAddOnIcon = (name = '') => {
  const key = name.toLowerCase().replace(/[\s()]/g, '').replace('500gm','').replace('gm','');
  return AddOnIcons[key] || AddOnIcons.default;
};

// ── Care icons ────────────────────────────────────────────────────────────────
const CARE_ICONS = {
  light:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/></svg>,
  water:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 2C6 9 4 13 4 16a8 8 0 0 0 16 0c0-3-2-7-8-14z"/></svg>,
  difficulty:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
  humidity:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 2C8 7 5 11 5 14a7 7 0 0 0 14 0c0-3-3-7-7-12z"/><path d="M7 15a5 5 0 0 0 5 4"/></svg>,
  temperature: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>,
  petFriendly: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="8" cy="8" r="2"/><circle cx="16" cy="8" r="2"/><circle cx="5" cy="14" r="1.8"/><circle cx="19" cy="14" r="1.8"/><path d="M12 13c-3 0-5.5 2-5.5 4.5S8.5 21 12 21s5.5-1 5.5-3.5S15 13 12 13z"/></svg>,
};

const DIFFICULTY_COLOR = {
  'Very Easy': '#22c55e',
  'Easy':      '#84cc16',
  'Moderate':  '#f59e0b',
  'Advanced':  '#ef4444',
};

// ── Relative time helper for real review timestamps ─────────────────────────
function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? '1 month ago' : `${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function Stars({ rating, size = 14 }) {
  return (
    <div className="pdp__stars">
      {[1,2,3,4,5].map(i => (
        <svg key={i} width={size} height={size} viewBox="0 0 24 24"
          fill={i <= Math.round(rating) ? '#F59E0B' : 'none'}
          stroke="#F59E0B" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      ))}
    </div>
  );
}

// Given a Shopify-style variant id from the URL, figure out which size /
// planter / color it points to. variantId can live on a pricing entry
// (size × planter combo, used when the product has no colors) or on a
// color option nested inside a planter (used when colors exist) — see
// the comments on Product.js's colorSchema / pricingSchema.
function resolveSelectionFromVariantId(product, variantId) {
  if (!product || !variantId) return null;

  // 1) Look for a matching color first — colors are the most specific
  for (const planter of product.planters || []) {
    const color = (planter.colors || []).find(c => c.variantId === variantId);
    if (color) {
      // Pick a size that's actually orderable with this planter, defaulting
      // to the first size in the product if none match (size-less product).
      const size = (product.sizes || []).find(s =>
        (product.pricing || []).some(p => p.sizeLabel === s.label && p.planterName === planter.name)
      ) || product.sizes?.[0] || null;
      return { size, planter, color };
    }
  }

  // 2) Fall back to a pricing-entry match (size × planter, no colors).
  const entry = (product.pricing || []).find(p => p.variantId === variantId);
  if (entry) {
    const size = (product.sizes || []).find(s => s.label === entry.sizeLabel) || null;
    const planter = (product.planters || []).find(pl => pl.name === entry.planterName) || null;
    return { size, planter, color: planter?.colors?.[0] || null };
  }

  return null;
}

// Look up the price for the currently selected size + planter combo from
// the product's pricing matrix. Falls back to the product's top-level price.
function resolvePrice(product, sizeLabel, planterName) {
  if (!product.pricing || product.pricing.length === 0) {
    return { price: product.price, originalPrice: product.originalPrice };
  }

  // Exact size + planter match
  const exact = product.pricing.find(
    p => p.sizeLabel === sizeLabel && p.planterName === planterName
  );
  if (exact) return { price: exact.price, originalPrice: exact.originalPrice };

  // Size-only match (products without planter variants store planterName as '')
  const sizeOnly = product.pricing.find(
    p => p.sizeLabel === sizeLabel && (p.planterName === '' || p.planterName == null)
  );
  if (sizeOnly) return { price: sizeOnly.price, originalPrice: sizeOnly.originalPrice };

  // First pricing entry as ultimate fallback
  const first = product.pricing[0];
  return { price: first.price, originalPrice: first.originalPrice };
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ProductDetailPage({ addToCart }) {
  const { slug } = useParams();
  const navigate  = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [product,   setProduct]   = useState(null);
  const [related,   setRelated]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // Selections
  const [qty,             setQty]             = useState(1);
  const [added,           setAdded]           = useState(false);
  const [selectedImg,     setSelectedImg]     = useState(0);
  const [selectedPlanter, setSelectedPlanter] = useState(null);
  const [selectedColor,   setSelectedColor]   = useState(null);
  const [selectedSize,    setSelectedSize]    = useState(null);

  // UI state
  const [specsOpen,          setSpecsOpen]          = useState(false);
  const [careExpanded,       setCareExpanded]        = useState(false);
  const [addedAddOns,        setAddedAddOns]         = useState({});
  const [openFaq,            setOpenFaq]             = useState(0);
  const [lightboxIndex,      setLightboxIndex]       = useState(null);

  // Real reviews fetched from the backend for this product
  const [reviews,       setReviews]       = useState([]);
  const [reviewsTotal,  setReviewsTotal]  = useState(0);
  const [ratingCounts,  setRatingCounts]  = useState({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
  const [reviewsPage,   setReviewsPage]   = useState(1);
  const [reviewsPages,  setReviewsPages]  = useState(1);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const reviewGallery = reviews.flatMap(r =>
    (r.images || []).map(img => ({ src: img.url, name: r.userName, rating: r.rating, date: timeAgo(r.createdAt), text: r.comment }))
  );

  const relatedTrackRef = useRef(null);
  const didInitRef = useRef(false); // guards against syncing the URL before the initial load picks defaults

  const scrollRelated = (dir) => {
    const el = relatedTrackRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('.pdp__related-card')?.offsetWidth || 220;
    el.scrollBy({ left: dir * (cardWidth + 16) * 2, behavior: 'smooth' });
  };

  // ── Fetch product from API ──────────────────────────────────────────────────
  useEffect(() => {
    // Capture the ?variant param NOW, synchronously, before any async work.
    // Reading it inside .then() is a race: the URL-sync effect can overwrite
    // it between fetch start and resolve, so the wrong variant gets selected
    // when navigating here from a cart link.
    const variantParamAtLoad = new URLSearchParams(window.location.search).get('variant');

    setLoading(true);
    setError(null);
    setProduct(null);
    setRelated([]);
    setSelectedImg(0);
    setSelectedPlanter(null);
    setSelectedColor(null);
    setSelectedSize(null);
    setSpecsOpen(false);
    setCareExpanded(false);
    setAddedAddOns({});
    setOpenFaq(0);
    setLightboxIndex(null);
    setQty(1);
    setAdded(false);
    didInitRef.current = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });

    fetch(`${BASE}/api/products/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : 'server_error');
        return r.json();
      })
      .then(({ product: p }) => {
        setProduct(p);
        trackEvent('ViewContent', {
          content_ids: [String(p._id || p.id || '')],
          content_name: p.name || '',
          content_type: 'product',
          value: p.price || 0,
          currency: 'INR',
        });

        // Initialise size selection from DB sizes array
        const firstSize = p.sizes?.[0] || null;
        setSelectedSize(firstSize);

        // Initialise planter + color selection — pick first planter available for firstSize
        const firstPlanter = firstSize
          ? (p.planters || []).find(pl =>
              (p.pricing || []).some(pr => pr.sizeLabel === firstSize.label && pr.planterName === pl.name)
            ) || p.planters?.[0] || null
          : p.planters?.[0] || null;
        setSelectedPlanter(firstPlanter);
        setSelectedColor(firstPlanter?.colors?.[0] || null);

        // If the URL was loaded with a Shopify-style ?variant=<id> (e.g. a
        // shared/bookmarked link or one of our own URLs after a selection
        // change below), honor it and override the defaults above.
        // Use variantParamAtLoad (captured synchronously above) \u2014 NOT
        // window.location.search here, which may have been overwritten by
        // the URL-sync effect firing between fetch start and resolve.
        const fromUrl = resolveSelectionFromVariantId(p, variantParamAtLoad);
        if (fromUrl) {
          if (fromUrl.size)    setSelectedSize(fromUrl.size);
          if (fromUrl.planter) setSelectedPlanter(fromUrl.planter);
          setSelectedColor(fromUrl.color || null);
        }
        didInitRef.current = true;

        // Fetch related products (same category, exclude this one)
        return fetch(`${BASE}/api/products?category=${encodeURIComponent(p.category)}`);
      })
      .then(r => r.json())
      .then(({ products }) => {
        setRelated(
          (products || [])
            .filter(p => p.slug !== slug)
            .slice(0, 8)
            .map(p => ({
              ...p,
              id: p._id || p.id,
              imageUrl: (p.images && p.images[0]?.url) || p.image || null,
            }))
        );
      })
      .catch(err => {
        setError(err.message === 'not_found' ? 'not_found' : 'error');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // When only the ?variant param changes (same slug, e.g. clicking a cart link
  // while already on this product page), apply the new selection without
  // re-fetching. The slug-effect handles fresh loads; this handles in-page
  // variant switches from external links like cart item clicks.
  const prevVariantRef = useRef(null);
  useEffect(() => {
    if (!product || !didInitRef.current) return;
    const variantParam = searchParams.get('variant');
    if (!variantParam || variantParam === prevVariantRef.current) return;
    prevVariantRef.current = variantParam;
    const fromUrl = resolveSelectionFromVariantId(product, variantParam);
    if (fromUrl) {
      if (fromUrl.size)    setSelectedSize(fromUrl.size);
      if (fromUrl.planter) setSelectedPlanter(fromUrl.planter);
      setSelectedColor(fromUrl.color || null);
    }
  }, [searchParams, product]);

  // ── Fetch real reviews once the product (and its _id) is known ─────────────
  useEffect(() => {
    if (!product?._id) { setReviews([]); return; }
    setReviewsLoading(true);
    setReviewsPage(1);
    getProductReviews(product._id, 1)
      .then(data => {
        setReviews(data.reviews || []);
        setReviewsTotal(data.total || 0);
        setRatingCounts(data.ratingCounts || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });
        setReviewsPages(data.pages || 1);
      })
      .catch(() => { setReviews([]); })
      .finally(() => setReviewsLoading(false));
  }, [product?._id]);

  const loadMoreReviews = () => {
    if (!product?._id || reviewsPage >= reviewsPages) return;
    const nextPage = reviewsPage + 1;
    setReviewsLoading(true);
    getProductReviews(product._id, nextPage)
      .then(data => {
        setReviews(prev => [...prev, ...(data.reviews || [])]);
        setReviewsPage(nextPage);
      })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  };


  // Keep the URL's ?variant=<id> in sync with the live selection
  // using replace so every swatch click doesn't pile up browser history entries.
  useEffect(() => {
    if (!didInitRef.current || !product) return;

    const pricingEntry = (product.pricing || []).find(
      p => p.sizeLabel === selectedSize?.label && p.planterName === (selectedPlanter?.name || '')
    );
    // Most specific wins: a selected color's own variant id, else the
    // size × planter pricing entry's variant id.
    const variantId = selectedColor?.variantId || pricingEntry?.variantId || null;
    if (!variantId) return;

    if (searchParams.get('variant') !== variantId) {
      const next = new URLSearchParams(searchParams);
      next.set('variant', variantId);
      setSearchParams(next, { replace: true });
    }
  }, [product, selectedSize, selectedPlanter, selectedColor, searchParams, setSearchParams]);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape')      setLightboxIndex(null);
      if (e.key === 'ArrowRight')  setLightboxIndex(i => (i + 1) % reviewGallery.length);
      if (e.key === 'ArrowLeft')   setLightboxIndex(i => (i - 1 + reviewGallery.length) % reviewGallery.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);

  // ── SEO (must be before any early returns to satisfy Rules of Hooks) ────────
  const _seoPrice = product ? (
    (() => {
      if (!product.pricing || product.pricing.length === 0) return product.price || 0;
      return product.pricing.reduce((min, e) => e.price < min ? e.price : min, product.pricing[0].price);
    })()
  ) : 0;
  useSEO({
    title: product ? `${product.name} — Buy Online` : 'Shop Plants',
    description: product
      ? `Buy ${product.name} online. ${(product.description || '').slice(0, 80)} Starting ₹${_seoPrice.toLocaleString('en-IN')}. Free delivery across India.`.slice(0, 160)
      : 'Browse premium indoor plants at Naturiq. Free delivery across India.',
    image: product?.images?.[0]?.url || product?.imageUrl || undefined,
    url: `/product/${slug}`,
    type: 'product',
    structuredData: product
      ? [
          buildProductSchema(product, _seoPrice),
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Shop', url: '/collections' },
            { name: product.name, url: `/product/${slug}` },
          ]),
        ]
      : null,
  });

  // ── Loading / error states ───────────────────────────────────────────────────
  if (loading) return (
    <div className="pdp">
      <div className="pdp__skel-wrap">
        <div className="pdp__skel-gallery">
          <div className="pdp__skel-main-img skel" />
          <div className="pdp__skel-thumbs">
            {[...Array(4)].map((_, i) => <div key={i} className="pdp__skel-thumb skel" />)}
          </div>
        </div>
        <div className="pdp__skel-info">
          <div className="pdp__skel-line skel" style={{ width: '55%', height: 14 }} />
          <div className="pdp__skel-line skel" style={{ width: '85%', height: 30, marginTop: 10 }} />
          <div className="pdp__skel-line skel" style={{ width: '40%', height: 26, marginTop: 14 }} />
          <div className="pdp__skel-line skel" style={{ width: '70%', height: 14, marginTop: 20 }} />
          <div className="pdp__skel-line skel" style={{ width: '90%', height: 14, marginTop: 8 }} />
          <div className="pdp__skel-line skel" style={{ width: '60%', height: 14, marginTop: 8 }} />
          <div className="pdp__skel-options">
            {[...Array(3)].map((_, i) => <div key={i} className="pdp__skel-opt skel" />)}
          </div>
          <div className="pdp__skel-line skel" style={{ width: '100%', height: 50, marginTop: 24, borderRadius: 12 }} />
        </div>
      </div>
    </div>
  );

  if (error === 'not_found') return (
    <div className="pdp__loading">
      <h2 style={{ color: 'var(--text-secondary)' }}>Product not found</h2>
      <button onClick={() => navigate('/collections/plants')} style={{ marginTop: 16 }}>Back to Shop</button>
    </div>
  );

  if (error) return (
    <div className="pdp__loading">
      <h2 style={{ color: 'var(--text-secondary)' }}>Something went wrong</h2>
      <button onClick={() => window.location.reload()} style={{ marginTop: 16 }}>Retry</button>
    </div>
  );

  if (!product) return null;

  // ── Derived values ────────────────────────────────────────────────────────────
  // Gallery: use real DB images, fall back to cover image repeated
  const coverUrl = (product.images && product.images[0]?.url) || product.image || '';
  const galleryUrls = product.images && product.images.length > 0
    ? product.images.map(img => img.url)
    : [coverUrl];

  // If the selected color/planter/size has an assigned gallery image, jump to it.
  // Most specific selection wins: Color > Planter's variant image > Size > cover image.
  // (Planter's `icon` is just the small selector-card thumbnail and is never used here.)
  const variantImageUrl =
    selectedColor?.image
    || selectedPlanter?.galleryImage || selectedPlanter?.image // legacy fallback
    || selectedSize?.image
    || null;
  const variantImageIdx = variantImageUrl
    ? galleryUrls.findIndex(url => url === variantImageUrl)
    : -1;
  const activeImgIdx = variantImageIdx !== -1 ? variantImageIdx : selectedImg;
  // The URL to show in the large viewer: variant image always wins when set, even if
  // it is not in the gallery array (e.g. a color image uploaded separately).
  const activeImgSrc = variantImageUrl || galleryUrls[activeImgIdx] || coverUrl;

  const { price: resolvedPrice, originalPrice: resolvedOriginal } = resolvePrice(
    product,
    selectedSize?.label || '',
    selectedPlanter?.name || ''
  );

  const discount = resolvedOriginal
    ? Math.round((1 - resolvedPrice / resolvedOriginal) * 100)
    : null;

  const faqs = product.faqs || [];

  const care = product.care || {};
  const allCare = [
    { key: 'light',       label: 'Light',        value: care.light },
    { key: 'water',       label: 'Water',         value: care.water },
    { key: 'difficulty',  label: 'Difficulty',    value: care.difficulty },
    { key: 'humidity',    label: 'Humidity',      value: care.humidity },
    { key: 'temperature', label: 'Temperature',   value: care.temperature },
    { key: 'petFriendly', label: 'Pet Friendly',  value: care.petFriendly },
  ].filter(item => item.value);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  // Returns true if a size has at least one pricing entry
  const isSizeAvailable = (sizeLabel) =>
    (product.pricing || []).some(p => p.sizeLabel === sizeLabel);

  // Returns total stock qty for a size (across all planters, or size-only)
  const getSizeQty = (sizeLabel) =>
    (product.pricing || [])
      .filter(p => p.sizeLabel === sizeLabel)
      .reduce((sum, p) => sum + (Number(p.qty) || 0), 0);

  // Returns true if a planter is available for the currently selected size
  const isPlanterAvailable = (planterName) => {
    if (!selectedSize) return true;
    return (product.pricing || []).some(
      p => p.sizeLabel === selectedSize.label && p.planterName === planterName
    );
  };

  // Returns stock qty for a planter under the currently selected size
  const getPlanterQty = (planterName) => {
    if (!selectedSize) return null;
    const entry = (product.pricing || []).find(
      p => p.sizeLabel === selectedSize.label && p.planterName === planterName
    );
    return entry ? Number(entry.qty) || 0 : 0;
  };

  // Returns true when the currently selected size+planter variant is out of stock
  const isCurrentVariantOutOfStock = (() => {
    if (!product) return true;
    // Always respect the top-level inStock flag — admin can mark a product
    // out of stock even when a pricing matrix exists (e.g. temporarily disabled)
    if (product.inStock === false) return true;
    // No pricing matrix — product is in stock (inStock flag already checked above)
    if (!product.pricing || product.pricing.length === 0) return false;
    // Find the pricing entry for the current size + planter selection
    const entry = (product.pricing || []).find(p => {
      if (selectedSize && selectedPlanter) {
        return p.sizeLabel === selectedSize.label && p.planterName === selectedPlanter.name;
      }
      if (selectedSize) return p.sizeLabel === selectedSize.label;
      return true;
    });
    if (!entry) return true; // no matching pricing entry = treat as out of stock
    return (Number(entry.qty) || 0) === 0;
  })();

  const handleSizeChange = (size) => {
    setSelectedSize(size);
    setSelectedImg(0);
    // Auto-select the first planter available for this size
    if (product.planters && product.planters.length > 0) {
      const firstAvailable = product.planters.find(pl =>
        (product.pricing || []).some(
          p => p.sizeLabel === size.label && p.planterName === pl.name
        )
      ) || null;
      setSelectedPlanter(firstAvailable);
      setSelectedColor(firstAvailable?.colors?.[0] || null);
    }
  };

  const handlePlanterChange = (planter) => {
    setSelectedPlanter(planter);
    setSelectedColor(planter.colors?.[0] || null);
    setSelectedImg(0);
  };

  const handleAddToCart = () => {
    if (isCurrentVariantOutOfStock) return;
    const activePricingEntry = (product.pricing || []).find(
      p => p.sizeLabel === selectedSize?.label && p.planterName === (selectedPlanter?.name || '')
    );
    const activeVariantId = selectedColor?.variantId || activePricingEntry?.variantId || '';
    addToCart({
      ...product,
      id:       product._id || product.id,
      imageUrl: variantImageUrl || coverUrl,
      price:    resolvedPrice,
      originalPrice: resolvedOriginal,
      _selectedSize:    selectedSize?.label,
      _selectedPlanter: selectedPlanter?.name,
      _selectedColor:   selectedColor?.label,
      _variantId: activeVariantId,
      _qty: qty,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleAddOnClick = (addOn) => {
    addToCart({
      // Identity
      _id:           addOn.productId || null,
      id:            addOn.productId || addOn.name,
      name:          addOn.name,
      slug:          addOn.slug          || '',
      // Pricing
      price:         addOn.price,
      originalPrice: addOn.originalPrice != null ? addOn.originalPrice : null,
      // Image
      imageUrl:      addOn.image         || null,
      // Variant selections — mapped to the field names the cart/checkout/drawer all read
      _selectedSize:    addOn.sizeLabel    || '',
      _selectedPlanter: addOn.planterName  || '',
      _selectedColor:   addOn.colorLabel   || '',
      // variantId for deep-linking back to the exact variant on the product page
      _variantId:   addOn.variantId   || '',
      // Extra color display data
      _colorHex:    addOn.colorHex    || '',
      _colorHex2:   addOn.colorHex2   || '',
      _colorType:   addOn.colorType   || 'solid',
      // Variant axis labels (e.g. "Size" / "Weight" / "Planter" / "Color")
      variant1Name: addOn.variant1Name || 'Size',
      variant2Name: addOn.variant2Name || 'Planter',
    });
    setAddedAddOns(prev => ({ ...prev, [addOn.name]: true }));
    setTimeout(() => setAddedAddOns(prev => ({ ...prev, [addOn.name]: false })), 2000);
  };

  const productId = product._id || product.id || '';

  return (
    <main className="pdp">
      {/* Breadcrumb */}
      <nav className="pdp__breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <Link to="/collections/plants">Shop</Link>
        <span>/</span>
        <span className="pdp__breadcrumb-current">{product.name}</span>
      </nav>

      {/* ── Main product section ── */}
      <section className="pdp__main">

        {/* Gallery */}
        <div className="pdp__gallery">
          <div className="pdp__thumbs">
            {galleryUrls.map((src, i) => (
              <button
                key={i}
                className={`pdp__thumb${activeImgIdx === i ? ' pdp__thumb--active' : ''}`}
                onClick={() => setSelectedImg(i)}
              >
                <img src={src} alt={`${product.name} view ${i + 1}`} />
              </button>
            ))}
          </div>
          <div className="pdp__img-main">
            {product.badge && <span className="pdp__badge">{product.badge}</span>}
            {discount && <span className="pdp__discount">−{discount}%</span>}
            <img src={activeImgSrc} alt={product.name} />
          </div>
        </div>

        {/* Info */}
        <div className="pdp__info">
          <h1 className="pdp__name">{product.name}</h1>

          {/* Rating */}
          <div className="pdp__rating-row">
            <Stars rating={product.rating} size={15} />
            <span className="pdp__rating-val">{product.rating}</span>
            <span className="pdp__rating-cnt">({product.reviews} reviews)</span>
          </div>

          {/* Product tags */}
          {(product.tags || []).length > 0 && (
            <div className="pdp__tags">
              {(product.tags || []).map((t, i) => {
                const label       = typeof t === 'string' ? t : (t.label || '');
                const color       = typeof t === 'object' && t.color       ? t.color       : '#2d6a4f';
                const bgColor     = typeof t === 'object' && t.bgColor     ? t.bgColor     : '#f0f7f2';
                const borderColor = typeof t === 'object' && t.borderColor ? t.borderColor : '#c6e0ce';
                const iconUrl     = typeof t === 'object' ? (t.iconUrl || '') : '';
                return (
                  <span
                    key={i}
                    className="pdp__tag"
                    style={{ color, background: bgColor, borderColor }}
                  >
                    {iconUrl && (
                      <img src={iconUrl} alt="" className="pdp__tag-icon" aria-hidden="true" />
                    )}
                    {label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Price */}
          <div className="pdp__price-row">
            <span className="pdp__price">₹{resolvedPrice.toLocaleString()}</span>
            {resolvedOriginal && (
              <>
                <span className="pdp__price-was">₹{resolvedOriginal.toLocaleString()}</span>
                <span className="pdp__price-save">Save ₹{(resolvedOriginal - resolvedPrice).toLocaleString()}</span>
              </>
            )}
          </div>

          {/* Care pills */}
          {allCare.length > 0 && (
            <>
              <div className="pdp__care-pills">
                {allCare.slice(0, 2).map(item => (
                  <span key={item.key} className="pdp__care-pill">
                    {CARE_ICONS[item.key]} {item.value}
                  </span>
                ))}
                {allCare.length > 2 && (
                  <button className="pdp__care-pill pdp__care-pill--viewall" onClick={() => setCareExpanded(true)}>
                    View all
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                )}
              </div>

              {careExpanded && (
                <div className="pdp__care-modal-overlay" onClick={() => setCareExpanded(false)}>
                  <div className="pdp__care-modal" onClick={e => e.stopPropagation()}>
                    <div className="pdp__care-modal-header">
                      <h3 className="pdp__care-modal-title">Product Details</h3>
                      <button className="pdp__care-modal-close" onClick={() => setCareExpanded(false)} aria-label="Close">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                    <p className="pdp__care-modal-product">{product.name}</p>
                    <div className="pdp__care-modal-grid">
                      {allCare.map(item => (
                        <div key={item.key} className="pdp__care-modal-card">
                          <span className="pdp__care-modal-icon">{CARE_ICONS[item.key]}</span>
                          <p className="pdp__care-label">{item.label}</p>
                          <p className="pdp__care-value"
                            style={item.key === 'difficulty' ? { color: DIFFICULTY_COLOR[item.value] } : {}}>
                            {item.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Size selector (from DB sizes[]) ── */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="pdp__selector">
              <div className="pdp__selector-header">
                <span className="pdp__selector-label">Select {product.variant1Name || 'Size'}</span>
                {selectedSize && (
                  <span className="pdp__selector-chosen">
                    {selectedSize.label}{selectedSize.desc ? ` · ${selectedSize.desc}` : ''}
                  </span>
                )}
              </div>
              <div className="pdp__size-pills">
                {product.sizes.filter(size => isSizeAvailable(size.label)).map(size => {
                  const sizeQty = getSizeQty(size.label);
                  const available = isSizeAvailable(size.label);
                  const outOfStock = !available || sizeQty === 0;
                  return (
                    <button
                      key={size.label}
                      className={`pdp__size-pill${selectedSize?.label === size.label ? ' pdp__size-pill--active' : ''}${outOfStock ? ' pdp__size-pill--oos' : ''}`}
                      onClick={() => { if (!outOfStock) handleSizeChange(size); }}
                      disabled={outOfStock}
                    >
                      <span className="pdp__size-pill-label">{size.label}</span>
                      {size.desc && <span className="pdp__size-pill-desc">{size.desc}</span>}
                      {!outOfStock && sizeQty <= 5 && sizeQty > 0 && (
                        <span className="pdp__stock-badge pdp__stock-badge--low">Only {sizeQty} left</span>
                      )}
                      {outOfStock && (
                        <span className="pdp__stock-badge pdp__stock-badge--out">Out of stock</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Planter selector (from DB planters[]) ── */}
          {product.planters && product.planters.length > 0 && (
            <div className="pdp__selector">
              <div className="pdp__selector-header">
                <span className="pdp__selector-label">Select {product.variant2Name || 'Planter'}</span>
                {selectedPlanter && (
                  <span className="pdp__selector-chosen">{selectedPlanter.name}</span>
                )}
              </div>
              <div className="pdp__planter-grid">
                {product.planters.filter(planter => isPlanterAvailable(planter.name)).map(planter => {
                  const planterQty = getPlanterQty(planter.name);
                  const planterAvailable = isPlanterAvailable(planter.name);
                  const planterOos = !planterAvailable || planterQty === 0;
                  const isActive = selectedPlanter?.name === planter.name;
                  // icon = small selector-card thumbnail; falls back to the legacy
                  // single `image` field for products saved before icon/galleryImage split.
                  // When this option is selected, prefer the dedicated "selected" icon
                  // (iconActive) if the admin uploaded one, else fall back to the
                  // regular icon so products without a second icon still work.
                  const planterIcon = (isActive && planter.iconActive) ? planter.iconActive : (planter.icon || planter.image || '');
                  return (
                    <button
                      key={planter.name}
                      className={`pdp__planter-card${!planterIcon ? ' pdp__planter-card--text-only' : ''}${isActive ? ' pdp__planter-card--active' : ''}${planterOos ? ' pdp__planter-card--oos' : ''}`}
                      onClick={() => { if (!planterOos) handlePlanterChange(planter); }}
                      disabled={planterOos}
                    >
                      {planterIcon ? (
                        <span className="pdp__planter-img-wrap">
                          <img src={planterIcon} alt={planter.name} className="pdp__planter-img" />
                        </span>
                      ) : null}
                      <span className="pdp__planter-name">{planter.name}</span>
                      {!planterOos && planterQty !== null && planterQty <= 5 && planterQty > 0 && (
                        <span className="pdp__stock-badge pdp__stock-badge--low">Only {planterQty} left</span>
                      )}
                      {planterOos && (
                        <span className="pdp__stock-badge pdp__stock-badge--out">Out of stock</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Color selector (from selected planter's colors[]) ── */}
          {selectedPlanter && selectedPlanter.colors && selectedPlanter.colors.length > 0 && (
            <div className="pdp__selector">
              <div className="pdp__selector-header">
                <span className="pdp__selector-label">Select Color</span>
                {selectedColor && <span className="pdp__selector-chosen">{selectedColor.label}</span>}
              </div>
              <div className="pdp__color-swatches">
                {selectedPlanter.colors.map(color => {
                  const isHalf = color.colorType === 'half';
                  const isGradient = color.colorType === 'gradient';
                  const isMulti = isHalf || isGradient;
                  return (
                    <button
                      key={color.label}
                      className={`pdp__color-swatch${selectedColor?.label === color.label ? ' pdp__color-swatch--active' : ''}`}
                      onClick={() => { setSelectedColor(color); setSelectedImg(0); }}
                      title={color.label}
                      aria-label={color.label}
                    >
                      {isHalf ? (
                        /* Top half / bottom half — hard horizontal split */
                        <span className="pdp__color-dot pdp__color-dot--split">
                          <span className="pdp__color-dot__top"    style={{ background: color.hex }} />
                          <span className="pdp__color-dot__bottom" style={{ background: color.hex2 || '#ffffff' }} />
                        </span>
                      ) : isGradient ? (
                        /* Smooth top-to-bottom gradient */
                        <span
                          className="pdp__color-dot"
                          style={{ background: `linear-gradient(to bottom, ${color.hex} 0%, ${color.hex2 || '#ffffff'} 100%)` }}
                        />
                      ) : (
                        /* Solid single color */
                        <span className="pdp__color-dot" style={{ background: color.hex }} />
                      )}
                      {selectedColor?.label === color.label && (
                        <svg className="pdp__color-check" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Add-ons (from DB addOns[]) ── */}
          {product.addOns && product.addOns.length > 0 && (
            <div className="pdp__addons">
              <h3 className="pdp__addons-title">Don't forget their add-ons!</h3>
              <div className="pdp__addons-list">
                {product.addOns.map((addOn, idx) => {
                  const isAdded = addedAddOns[addOn.name];
                  // Build color swatch background if a color was selected
                  const colorBg = addOn.colorHex
                    ? addOn.colorType === 'gradient'
                      ? `linear-gradient(135deg, ${addOn.colorHex} 50%, ${addOn.colorHex2 || addOn.colorHex} 50%)`
                      : addOn.colorType === 'half'
                      ? `linear-gradient(to right, ${addOn.colorHex} 50%, ${addOn.colorHex2 || addOn.colorHex} 50%)`
                      : addOn.colorHex
                    : null;
                  return (
                    <div key={`${addOn.name}-${idx}`} className="pdp__addon-card">
                      {/* Variant-resolved product image */}
                      <div className="pdp__addon-thumb">
                        {addOn.image
                          ? <img src={addOn.image} alt={addOn.name} className="pdp__addon-thumb-img" />
                          : <span className="pdp__addon-thumb-emoji">{getAddOnIcon(addOn.name)}</span>
                        }
                      </div>
                      <div className="pdp__addon-info">
                        {/* Rating */}
                        {addOn.rating > 0 && (
                          <div className="pdp__addon-rating">
                            <Stars rating={addOn.rating} size={12} />
                            <span>{Number(addOn.rating).toFixed(1)} | {addOn.reviews}</span>
                          </div>
                        )}
                        {/* Name — links to product page */}
                        {addOn.slug
                          ? <Link to={`/product/${addOn.slug}`} className="pdp__addon-name pdp__addon-name--link">{addOn.name}</Link>
                          : <p className="pdp__addon-name">{addOn.name}</p>
                        }
                        {/* Selected variant chips */}
                        {(addOn.sizeLabel || addOn.planterName || addOn.colorLabel) && (
                          <div className="pdp__addon-variants">
                            {addOn.sizeLabel && (
                              <span className="pdp__addon-variant-chip">
                                {addOn.variant1Name || 'Size'}: {addOn.sizeLabel}
                              </span>
                            )}
                            {addOn.planterName && (
                              <span className="pdp__addon-variant-chip">
                                {addOn.variant2Name || 'Planter'}: {addOn.planterName}
                              </span>
                            )}
                            {addOn.colorLabel && (
                              <span className="pdp__addon-variant-chip pdp__addon-variant-chip--color">
                                {colorBg && <span className="pdp__addon-color-dot" style={{ background: colorBg }} />}
                                {addOn.colorLabel}
                              </span>
                            )}
                          </div>
                        )}
                        {addOn.blurb && <p className="pdp__addon-blurb">{addOn.blurb}</p>}
                        <div className="pdp__addon-price-row">
                          <span className="pdp__addon-price">₹{addOn.price}</span>
                          {addOn.originalPrice && (
                            <span className="pdp__addon-was">₹{addOn.originalPrice}</span>
                          )}
                        </div>
                      </div>
                      <button
                        className={`pdp__addon-btn${isAdded ? ' pdp__addon-btn--added' : ''}`}
                        onClick={() => handleAddOnClick(addOn)}
                        aria-label={`Add ${addOn.name} to cart`}
                      >
                        {isAdded ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Qty + CTA */}
          <div className="pdp__actions">
            <div className="pdp__qty">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="Decrease">−</button>
              <span>{qty}</span>
              <button onClick={() => setQty(q => q + 1)} aria-label="Increase">+</button>
            </div>
            <button
              className={`pdp__add-btn${added ? ' pdp__add-btn--added' : ''}`}
              onClick={handleAddToCart}
              disabled={isCurrentVariantOutOfStock}
            >
              {added
                ? <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> Added!</>
                : isCurrentVariantOutOfStock ? 'Out of Stock' : 'Add to Cart'
              }
            </button>
          </div>

          {/* Trust strip */}
          <div className="pdp__trust">
            <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> Free delivery over ₹599</span>
            <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> 7-day healthy plant guarantee</span>
            <span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> All over India delivery</span>
          </div>

          {/* ── Product Specifications ── */}
          <div className="pdp__specs">
            <button
              className={`pdp__specs-toggle${specsOpen ? ' pdp__specs-toggle--open' : ''}`}
              onClick={() => setSpecsOpen(o => !o)}
              aria-expanded={specsOpen}
            >
              <span>Product Specifications</span>
              <svg className="pdp__specs-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            <div className={`pdp__specs-body${specsOpen ? ' pdp__specs-body--open' : ''}`}>
              <table className="pdp__specs-table">
                <tbody>
                  {[
                    product.category && ['Category',  product.category.charAt(0).toUpperCase() + product.category.slice(1)],
                    ['Name',           product.name],
                    selectedPlanter && [product.variant2Name || 'Variant 2',       selectedPlanter.name],
                    selectedColor   && [`${product.variant2Name || 'Variant 2'} Color`, selectedColor.label],
                    selectedSize    && [product.variant1Name || 'Variant 1',       `${selectedSize.label}${selectedSize.desc ? ` (${selectedSize.desc})` : ''}`],
                    care.difficulty  && ['Difficulty',   care.difficulty],
                    care.light       && ['Light',         care.light],
                    care.water       && ['Water',         care.water],
                    care.humidity    && ['Humidity',      care.humidity],
                    care.temperature && ['Temperature',   care.temperature],
                    care.petFriendly && ['Pet Friendly',  care.petFriendly],
                    selectedPlanter
                      ? ['In The Box', `${product.name} + ${selectedPlanter.name}`]
                      : ['In The Box', product.name],
                    productId && ['SKU', `NTQ-${productId.toString().slice(-4).toUpperCase()}`],
                  ].filter(Boolean).map(([label, value]) => (
                    <tr key={label} className="pdp__specs-row">
                      <td className="pdp__specs-label">{label}</td>
                      <td className="pdp__specs-value">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* ── Related products ── */}
      {related.length > 0 && (
        <section className="pdp__related">
          <div className="pdp__related-header">
            <h2 className="pdp__related-title">You may also like</h2>
            <div className="pdp__related-nav">
              <button type="button" className="pdp__related-nav-btn" onClick={() => scrollRelated(-1)} aria-label="Scroll left">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button type="button" className="pdp__related-nav-btn" onClick={() => scrollRelated(1)} aria-label="Scroll right">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>
          <div className="pdp__related-grid" ref={relatedTrackRef}>
            {related.map(p => (
              <div key={p.id || p._id} className="pdp__related-card">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Reviews ── */}
      <section className="pdp__section">
        <h2 className="pdp__reviews-heading">Customer Reviews</h2>
        <div className="pdp__reviews-summary">
          <div className="pdp__reviews-score">
            <div className="pdp__reviews-score-row">
              <Stars rating={product.rating} size={20} />
              <span className="pdp__reviews-big">{product.rating || 0} out of 5</span>
            </div>
            <span className="pdp__reviews-total">Based on {reviewsTotal} review{reviewsTotal !== 1 ? 's' : ''}</span>
          </div>
          <div className="pdp__reviews-bars">
            {[5,4,3,2,1].map(n => {
              const count = ratingCounts[n] || 0;
              const pct = reviewsTotal > 0 ? Math.round((count / reviewsTotal) * 100) : 0;
              return (
                <div key={n} className="pdp__reviews-bar-row">
                  <span className="pdp__reviews-bar-stars">
                    {n}<svg width="11" height="11" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1.5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  </span>
                  <div className="pdp__reviews-bar-track">
                    <div className="pdp__reviews-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="pdp__reviews-bar-count">{pct}%</span>
                </div>
              );
            })}
          </div>
          <div className="pdp__reviews-trust">
            <span className="pdp__reviews-trust-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </span>
            <div>
              <p className="pdp__reviews-trust-title">100% Verified Buyers</p>
              <p className="pdp__reviews-trust-text">Every review is from a customer who actually purchased and received this plant.</p>
            </div>
          </div>
        </div>

        {/* Customer photo gallery — built from real review photo uploads */}
        {reviewGallery.length > 0 && (
          <div className="pdp__review-gallery">
            {reviewGallery.map((photo, i) => {
              const isDesktopOverflowSlot = i === 11 && reviewGallery.length > 12;
              const isMobileOverflowSlot  = i === 5  && reviewGallery.length > 6;
              return (
                <button
                  key={i}
                  className={`pdp__review-gallery-thumb${i >= 12 ? ' pdp__review-gallery-thumb--overflow' : ''}${i >= 6 ? ' pdp__review-gallery-thumb--mobile-overflow' : ''}`}
                  onClick={() => setLightboxIndex(i)}
                  aria-label={`View customer photo ${i + 1}`}
                >
                  <img src={photo.src} alt={`Photo from ${photo.name}'s review`} />
                  {isDesktopOverflowSlot && (
                    <span className="pdp__review-gallery-more pdp__review-gallery-more--desktop">+{reviewGallery.length - 12}</span>
                  )}
                  {isMobileOverflowSlot && (
                    <span className="pdp__review-gallery-more pdp__review-gallery-more--mobile">+{reviewGallery.length - 6}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {lightboxIndex !== null && reviewGallery[lightboxIndex] && (
          <div className="pdp__lightbox-overlay" onClick={() => setLightboxIndex(null)}>
            <button className="pdp__lightbox-close" onClick={() => setLightboxIndex(null)} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <button className="pdp__lightbox-nav pdp__lightbox-nav--prev"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + reviewGallery.length) % reviewGallery.length); }}
              aria-label="Previous photo">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div className="pdp__lightbox-card" onClick={(e) => e.stopPropagation()}>
              <img src={reviewGallery[lightboxIndex].src} alt={`Photo from ${reviewGallery[lightboxIndex].name}'s review`} className="pdp__lightbox-img" />
              <div className="pdp__lightbox-details">
                <div className="pdp__lightbox-details-header">
                  <div className="pdp__review-avatar">{(reviewGallery[lightboxIndex].name || '?')[0]}</div>
                  <div>
                    <p className="pdp__review-name">{reviewGallery[lightboxIndex].name}</p>
                    <Stars rating={reviewGallery[lightboxIndex].rating} size={13} />
                  </div>
                  <span className="pdp__review-date">{reviewGallery[lightboxIndex].date}</span>
                </div>
                <p className="pdp__review-text">{reviewGallery[lightboxIndex].text}</p>
              </div>
            </div>
            <button className="pdp__lightbox-nav pdp__lightbox-nav--next"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % reviewGallery.length); }}
              aria-label="Next photo">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        )}

        {reviews.length === 0 && !reviewsLoading && (
          <div className="pdp__reviews-empty">
            <p>No reviews yet for this plant.</p>
            <p>Reviews appear here once buyers who received this product write one from their Orders page.</p>
          </div>
        )}

        <div className="pdp__reviews-list">
          {reviews.map((r) => (
            <div key={r._id} className="pdp__review-card">
              <div className="pdp__review-header">
                <div className="pdp__review-avatar">{(r.userName || '?')[0]}</div>
                <div>
                  <p className="pdp__review-name">
                    {r.userName || 'Verified Buyer'}
                    {r.verifiedPurchase && <span> · Verified Purchase</span>}
                  </p>
                  <Stars rating={r.rating} size={13} />
                </div>
                <span className="pdp__review-date">{timeAgo(r.createdAt)}</span>
              </div>
              {r.title && <p className="pdp__review-title" style={{ fontWeight: 600, margin: '6px 0 2px' }}>{r.title}</p>}
              <p className="pdp__review-text">{r.comment}</p>
              {r.images && r.images.length > 0 && (
                <div className="pdp__review-photos">
                  {r.images.map((img, pi) => {
                    const galleryIdx = reviewGallery.findIndex(g => g.src === img.url);
                    return (
                      <button key={pi} className="pdp__review-photo-thumb"
                        onClick={() => setLightboxIndex(galleryIdx >= 0 ? galleryIdx : 0)}
                        aria-label={`View photo ${pi + 1} from ${r.userName}'s review`}>
                        <img src={img.url} alt={`${r.userName}'s review photo ${pi + 1}`} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {reviewsPage < reviewsPages && (
          <button className="pdp__reviews-viewmore-btn" onClick={loadMoreReviews} disabled={reviewsLoading}>
            {reviewsLoading ? 'Loading…' : `View more reviews (${reviewsTotal - reviews.length})`}
            <svg className="pdp__faq-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        )}
      </section>


      {/* ── FAQs (fully sourced from DB faqs[]) ── */}
      {faqs.length > 0 && (
        <section className="pdp__section">
          <h2 className="pdp__section-title">FAQs</h2>
          <p className="pdp__faq-subtitle">Everything you need to know before you bring this plant home.</p>
          <div className="pdp__faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`pdp__faq-item${openFaq === i ? ' pdp__faq-item--open' : ''}`}>
                <button
                  className="pdp__faq-question"
                  onClick={() => setOpenFaq(o => (o === i ? null : i))}
                  aria-expanded={openFaq === i}
                >
                  <span className="pdp__faq-q-text">{faq.q}</span>
                  <span className="pdp__faq-icon" aria-hidden="true">
                    <svg className={`pdp__faq-chevron${openFaq === i ? ' pdp__faq-chevron--open' : ''}`}
                      width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </span>
                </button>
                <div className={`pdp__faq-answer${openFaq === i ? ' pdp__faq-answer--open' : ''}`}>
                  <div className="pdp__faq-answer-inner">
                    <p>{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
